// ink-component
import React, { ButtonHTMLAttributes, ReactNode, MouseEvent, useContext, RefObject } from 'react';
import useInkAnalytics from '../../../../hooks/useInkAnalytics';

import DropdownContext from '../../contexts/DropdownContext';

export interface DropdownButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The text of the button */
  children?: ReactNode;
  /** onClick callback */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** React ref object to be forwarded to the internal node */
  externalRef?: RefObject<HTMLButtonElement>;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
  'data-track-name'?: string;
  /** Custom payload to be sent to analytics provider */
  'data-track-payload'?: any;
  /** List of tracked events in the analytics integration */
  'data-track-events'?: string[];
  /** @ignore */
  render?: () => void;
}

/**
 * @purpose A button for Dropdown.Item(s)
 * @categories navigation
 * @group Dropdown
 * @HTMLType HTMLButtonElement
 */
function DropdownButton(props: DropdownButtonProps) {
  const { close, preventClose } = useContext(DropdownContext);
  const { render, externalRef, ...otherProps } = props;
  const [ref] = useInkAnalytics(props, { ref: externalRef });

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!event) {
      throw new Error(
        `When extending this component's onClick handler, you need to forward the event to the original handler.`,
      );
    }

    event.stopPropagation();

    if (props.onClick) {
      props.onClick(event);
    }
    if (!preventClose) {
      close();
    }
  };

  return (
    <button {...otherProps} ref={ref} onClick={handleClick}>
      {props.children}
    </button>
  );
}

DropdownButton.defaultProps = {
  children: undefined,
  onClick: undefined,
  'data-track-events': ['click'],
};

DropdownButton.displayName = 'Dropdown.Button';

export default DropdownButton;
